import { UserCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { useTranslations } from '@keymono/i18n';
import { useAuthStore } from '@keymono/services';
import { useUpdateUserDetailsMutation } from '@keymono/apis';
import {
  FirstNameInputField,
  Button,
  FormFeedbackSection,
  LastNameInputField,
  TextInputField,
  useForm,
} from '@keymono/design-system';

import { CardWrapper } from '../styles';

interface IUpdateUserDetailsFormInputFields {
  firstName: string;
  lastName: string;
  otherName: string;
}

interface INamesCardEditProps {
  onExitEditMode: () => void;
}

/**
 * -----------------------------------------------------------------------------
 * This card allows for the user to update their names.
 * TODO: Refactor more to merge common items with NamesCard
 */
export function NamesCardEdit({ onExitEditMode }: INamesCardEditProps) {
  const t = useTranslations('UserProfile.namesSection');
  const user = useAuthStore().activeSession?.user;

  const handleCancelEdit = () => {
    onExitEditMode();
  };

  const handleOnUpdateNamesSuccess = () => {
    onExitEditMode();
  };

  const {
    error,
    isLoading: isFormSubmitting,
    updateUserDetails,
  } = useUpdateUserDetailsMutation({
    onSuccessCallback: handleOnUpdateNamesSuccess,
  });

  const errorFields = error?.errorFields;

  const handlePostDetails = (values: IUpdateUserDetailsFormInputFields) => {
    updateUserDetails({
      userId: user?.id || '',
      first_name: values.firstName || '',
      last_name: values.lastName,
      other_name: values.otherName,
    });
  };

  /**
   * The fields to be tracked within the form.
   */
  const namesInputFieldsValues: IUpdateUserDetailsFormInputFields = {
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    otherName: '',
  };

  const {
    values,
    errors,
    handleOnInputChange,
    handleOnBlur,
    handleOnError,
    handleOnSubmitForm,
  } = useForm({
    initialValues: namesInputFieldsValues,
    onSubmitCb: handlePostDetails,
  });

  return (
    <CardWrapper className="group">
      <div>
        <span className="inline-flex rounded-lg bg-red-50 p-3 text-red-700 ring-4 ring-white">
          <UserCircleIcon className="h-6 w-6" aria-hidden="true" />
        </span>
      </div>
      <form className="mt-5 sm:mt-6" onSubmit={handleOnSubmitForm}>
        {error?.message ? (
          <FormFeedbackSection
            isFormSubmitting={isFormSubmitting}
            type="error"
            message={error.message}
            description={error.description}
          />
        ) : (
          <FormFeedbackSection
            isFormSubmitting={isFormSubmitting}
            type="info"
            message={t('editNamesFormIntro.message')}
            description={t('editNamesFormIntro.description')}
          />
        )}
        <FirstNameInputField
          className="mt-4"
          error={errorFields?.first_name || errors.firstName}
          label={t('formFields.firstName.label')}
          name="firstName"
          onBlurCallback={handleOnBlur}
          onChangeCallback={handleOnInputChange}
          onError={handleOnError}
          placeholder={t('formFields.firstName.placeholder')}
          value={values.firstName}
        />
        <LastNameInputField
          error={errorFields?.last_name || errors.lastName}
          label={t('formFields.lastName.label')}
          name="lastName"
          onBlurCallback={handleOnBlur}
          onChangeCallback={handleOnInputChange}
          onError={handleOnError}
          placeholder={t('formFields.lastName.placeholder')}
          value={values.lastName}
        />
        <TextInputField
          // TODO: Remove this field :: OTHER
          constraints={{ required: false }}
          type="text"
          error={errorFields?.other_name || errors.otherName}
          label={t('formFields.otherName.label')}
          name="otherName"
          onBlurCallback={handleOnBlur}
          onChangeCallback={handleOnInputChange}
          onError={handleOnError}
          placeholder={t('formFields.otherName.placeholder')}
          value={values.otherName}
        />

        <div className="mt-2 flex w-full justify-end gap-2">
          <button
            type="button"
            onClick={handleCancelEdit}
            className="form-btn-secondary w-auto py-1.5 text-xs"
            disabled={isFormSubmitting}
          >
            {t('formFields.cancelCTA.label')}
          </button>
          <Button
            type="submit"
            className="form-btn w-auto px-8 py-1 text-xs"
            isEnabled={!isFormSubmitting}
            isLoading={isFormSubmitting}
            label={t('formFields.submitCTA.label')}
            loadingLabel={t('formFields.submitCTA.loadingLabel')}
          />
        </div>
      </form>
      <button
        type="button"
        title={t('closeCTATitle')}
        className="
          form-link-action
          absolute
          right-6 top-6 flex cursor-pointer rounded-lg
          p-2
          text-gray-300
          opacity-40
          hover:bg-red-100
          hover:text-red-700
          group-hover:text-red-700 group-hover:opacity-100
        "
        aria-hidden="true"
        onClick={handleCancelEdit}
      >
        <XMarkIcon className="h-6 w-6" />
      </button>
    </CardWrapper>
  );
}
